import { useEffect, useState } from 'react';
import Hero from './components/Hero';
import Navigation from './components/Navigation';
import Schedule from './components/Schedule';
import Ticket from './components/Ticket';
import Goods from './components/Goods';
import Information from './components/Information';
import Footer from './components/Footer';
import LoadingScreen from '../../components/feature/LoadingScreen';
import GoodsModal from './components/GoodsModal';

export default function Home() {
  const [activeSection, setActiveSection] = useState('hero');
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageType, setImageType] = useState<'venue' | 'benefit' | 'goods'>('goods');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'schedule', 'ticket', 'goods', 'information'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLoadingComplete = () => {
    setShowContent(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  };

  return (
    <>
      {isLoading && <LoadingScreen onLoadingComplete={handleLoadingComplete} />}
      
      <div className={`bg-[#0a0a0a] text-white font-press-start min-h-screen transition-opacity duration-1000 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
        <Navigation activeSection={activeSection} scrollToSection={scrollToSection} />
        
        {/* Background container for all sections except header and footer */}
        <div className="relative bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0a0a1e] overflow-hidden">
          {/* Enhanced Pixel Grid Background */}
          <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
            backgroundImage: `
              linear-gradient(0deg, transparent 24%, rgba(68, 196, 255, 0.3) 25%, rgba(68, 196, 255, 0.3) 26%, transparent 27%, transparent 74%, rgba(30, 249, 157, 0.3) 75%, rgba(30, 249, 157, 0.3) 76%, transparent 77%, transparent),
              linear-gradient(90deg, transparent 24%, rgba(68, 196, 255, 0.3) 25%, rgba(68, 196, 255, 0.3) 26%, transparent 27%, transparent 74%, rgba(30, 249, 157, 0.3) 75%, rgba(30, 249, 157, 0.3) 76%, transparent 77%, transparent)
            `,
            backgroundSize: '50px 50px'
          }}></div>

          {/* Animated Moving Colorful Pixels */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(50)].map((_, i) => {
              const randomX = Math.random() * 100;
              const randomY = Math.random() * 100;
              const randomDuration = 8 + Math.random() * 12;
              const randomDelay = Math.random() * 5;
              const color = ['#44c4ff', '#1ef99d', '#fc457e', '#fcb122'][Math.floor(Math.random() * 4)];
              
              return (
                <div
                  key={i}
                  className="absolute w-3 h-3"
                  style={{
                    left: `${randomX}%`,
                    top: `${randomY}%`,
                    backgroundColor: color,
                    boxShadow: `0 0 15px ${color}`,
                    animation: `floatPixel${i % 4} ${randomDuration}s ease-in-out ${randomDelay}s infinite`
                  }}
                ></div>
              );
            })}
          </div>

          <style>{`
            @keyframes floatPixel0 {
              0%, 100% { transform: translate(0, 0); }
              25% { transform: translate(30px, -40px); }
              50% { transform: translate(-20px, -80px); }
              75% { transform: translate(-40px, -40px); }
            }
            @keyframes floatPixel1 {
              0%, 100% { transform: translate(0, 0); }
              25% { transform: translate(-30px, 40px); }
              50% { transform: translate(20px, 80px); }
              75% { transform: translate(40px, 40px); }
            }
            @keyframes floatPixel2 {
              0%, 100% { transform: translate(0, 0); }
              25% { transform: translate(40px, 30px); }
              50% { transform: translate(80px, -20px); }
              75% { transform: translate(40px, -40px); }
            }
            @keyframes floatPixel3 {
              0%, 100% { transform: translate(0, 0); }
              25% { transform: translate(-40px, -30px); }
              50% { transform: translate(-80px, 20px); }
              75% { transform: translate(-40px, 40px); }
            }
          `}</style>
          
          <div className="relative z-10">
            <Hero scrollToSection={scrollToSection} />
            <Schedule />
            <Ticket
              onImageClick={(url) => { setImageType('benefit'); setSelectedImage(url); }}
              onVenueImageClick={(url) => { setImageType('venue'); setSelectedImage(url); }}
            />
            <Goods onImageClick={(url) => { setImageType('goods'); setSelectedImage(url); }} />
            <Information />
          </div>
        </div>
        
        <Footer />
      </div>

      {/* Modal outside all sections */}
      <GoodsModal 
        selectedImage={selectedImage}
        imageType={imageType}
        onClose={() => { setSelectedImage(null); }} 
      />
    </>
  );
}
